import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { PermIdentity } from '@mui/icons-material';
import Section from './Section';
import BkTable from 'src/components/table/BkTable';
import { Column } from 'src/utils/constants/table-constants';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import {
  acceptInvitation,
  deactivateMember,
  demoteMember,
  denyInvitation,
  editCompanyInfo,
  getCompanyInfo,
  getCompanyMembers,
  inviteMember,
  promoteMember,
  reinviteMember,
} from 'src/features/company/utils';
import { Box, Button, Menu, MenuItem, Skeleton } from '@mui/material';
import { CompanyInfo, initialState } from 'src/features/company/constants';
import AddNewCompanyUser from './AddNewCompanyUser';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditCompanyData from './EditCompanyData';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import CompanyInfoSkeleton from './CompanyInfoSkeleton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Typography } from 'src/components/new/Typography';
import { Button as NewButton } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { countryPhoneInfo, stateMapper } from 'src/utils/constant';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { format } from '@react-input/mask';

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const columns = [
  { headerName: 'Name', field: 'name' },
  { headerName: 'Email', field: 'email' },
  { headerName: 'Role', field: 'role' },
  { headerName: 'Actions', field: 'actions' },
];

const CompanyTab = () => {
  const dispatch: AppDispatch = useDispatch();

  const companyMembers = useSelector((state: RootState) => state.company.members);

  const isProcessingInvitation = useSelector((state: RootState) => state.company.isProcessingInvitation);

  const companyInfo = useSelector((state: RootState) => state.company.info);

  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const [showNewUser, setShowNewUser] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [selectedUserRole, setSelectedUserRole] = useState<string | null>(null);

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);

  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const shouldFetchCompanyInfo = companyInfo === initialState.info || companyInfo.id !== currentUser.organization.id;
  const shouldFetchCompanyMembers =
    companyMembers === initialState.members || companyInfo.id !== currentUser.organization.id;
  useEffect(() => {
    if (shouldFetchCompanyInfo) dispatch(getCompanyInfo());

    if (shouldFetchCompanyMembers) dispatch(getCompanyMembers());
  }, [companyInfo, companyMembers, showSnackbarMsg, dispatch]);

  const onAccept = async (id: string) => {
    try {
      setSelectedUserId(id);
      let response = await dispatch(acceptInvitation(id));

      if (response.meta.requestStatus === 'fulfilled') {
        showSnackBarMsg('Successfully accepted user into organization', 'success');
      } else {
        showSnackBarMsg('An error occured accepting the user into the organization. Please try again later!', 'error');
      }
    } catch (error) {}
  };

  const onDeny = async (id: string) => {
    try {
      setSelectedUserId(id);
      let response = await dispatch(denyInvitation(id));

      if (response.meta.requestStatus === 'fulfilled') {
        showSnackBarMsg('Successfully denied user from joining organization', 'success');
      } else {
        showSnackBarMsg(
          'An error occured denying the user from joining the organization. Please try again later!',
          'error',
        );
      }
    } catch (error) {}
  };

  const menuItems = (role: string, userId: string) => {
    const items = [];

    if (role === 'admin') {
      items.push({
        label: 'Make User',
        onClick: () => handleMenuAction('demote'),
      });
    }

    if (role === 'user') {
      items.push({
        label: 'Make Admin',
        onClick: () => handleMenuAction('promote'),
      });
    }

    items.push({
      label: 'Deactivate',
      onClick: () => handleMenuAction('deactivate'),
      disabled: role === 'admin',
      tooltip: role === 'admin' ? 'Change status to User to enable deactivation' : '',
    });

    return items;
  };

  const handleAddNewUser = () => {
    dispatch(inviteMember()).then((result) => {
      setShowNewUser(false);
      if (inviteMember.fulfilled.match(result)) {
        dispatch(getCompanyMembers());
        setShowSnackbarMsg({
          msg: 'Successfully invited new user',
          type: 'success',
        });
      } else {
        setShowSnackbarMsg({
          msg: typeof result.payload === 'string' ? result.payload : 'Something went wrong',
          type: 'error',
        });
      }
    });
  };

  const handleReinviteUser = (email: string) => {
    dispatch(reinviteMember(email)).then((result) => {
      setShowNewUser(false);
      if (reinviteMember.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Successfully reinvited new user',
          type: 'success',
        });
      } else {
        setShowSnackbarMsg({
          msg: typeof result.payload === 'string' ? result.payload : 'Something went wrong',
          type: 'error',
        });
      }
    });
  };

  const showSnackBarMsg = (msg: string, type: 'success' | 'error') => {
    setShowSnackbarMsg({ msg, type });

    setTimeout(() => {
      setShowSnackbarMsg({ msg: '', type });
    }, 5000);
  };

  type ActionType = 'promote' | 'demote' | 'deactivate';

  const handleMenuAction = (action: ActionType) => {
    const selectedUser = companyMembers.find((item) => item.id === Number(selectedUserId));

    const actionMap: Record<ActionType, { dispatchAction: any; successMsg: string; errorMsg: string }> = {
      promote: {
        dispatchAction: promoteMember({
          memberId: selectedUserId as string,
          role: 'admin',
        }),
        successMsg: `Successfully promoted: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when promoting: ${selectedUser?.email}`,
      },
      demote: {
        dispatchAction: demoteMember({
          memberId: selectedUserId as string,
          role: 'user',
        }),
        successMsg: `Successfully demoted: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when demoting:${selectedUser?.email}`,
      },
      deactivate: {
        dispatchAction: deactivateMember(selectedUserId as string),
        successMsg: `Successfully deactivated: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when deactivating: ${selectedUser?.email}`,
      },
    };

    const selectedAction = actionMap[action];

    if (selectedAction) {
      dispatch(selectedAction.dispatchAction).then((result: any) => {
        const resolvedTypes = result.type.split('/');

        const resolvedType = resolvedTypes[resolvedTypes.length - 1];

        switch (resolvedType) {
          case 'fulfilled':
            showSnackBarMsg(selectedAction.successMsg, 'success');
            resolvedTypes[1] === 'deactivateMember' && dispatch(getCompanyMembers());
            break;
          case 'rejected':
            showSnackBarMsg(selectedAction.errorMsg, 'error');
            break;
          default:
            return;
        }
      });
    }

    handleMenuClose();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: string, role: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
    setSelectedUserRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
    setSelectedUserRole(null);
  };

  const onEdit = (newCompanyInfo: CompanyInfo) => {
    const stringDomains = newCompanyInfo.domains.map((domain) => domain.name);
    const idDomains = newCompanyInfo.domains.map((domain) => domain.id).filter(Number);

    const formattedCompanyInfo = {
      ...newCompanyInfo,
      domains: {
        domainNames: stringDomains.filter((domain) => !companyInfo.domains.some((d) => d.name === domain)),
        deleteIds: companyInfo.domains.filter((domain) => !idDomains.includes(domain.id)).map((item) => item.id),
      },
    };

    dispatch(editCompanyInfo(formattedCompanyInfo)).then((result) => {
      if (editCompanyInfo.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Successfully update the company profile!',
          type: 'success',
        });
        setShowEditDialog(false);
      }
      if (editCompanyInfo.rejected.match(result)) {
        setShowSnackbarMsg({
          msg:  (result?.payload as string) ?? 'Something went wrong when updating the company profile!',
          type: 'error',
        });
      }
      dispatch(getCompanyInfo());
    });
  };

  const renderAction = (status: 'joined' | 'invited' | 'pending', id: string, role: string) => {
    switch (status) {
      case 'joined':
        const isOnlyAdmin =
          companyMembers.filter((member) => member.role === 'admin').length === 1 &&
          companyMembers.find((user) => user.id === Number(id))?.role === 'admin';

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              sx={{
                color: '#273194',
                textTransform: 'none',
                maxHeight: '2.5rem',
                width: isOnlyAdmin ? '6.25rem' : '16.5625rem',
              }}
              onClick={(event) => handleMenuOpen(event, id, role)}
              disabled={isOnlyAdmin}
            >
              <p>Edit Status</p>
            </Button>
            {isOnlyAdmin && (
              <BkToolTip
                title="Organization needs at least one Admin. Promote other user to Admin to enable Edit Status functionality"
                Icon={InfoOutlinedIcon}
              ></BkToolTip>
            )}
          </div>
        );
      case 'pending':
        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.25rem' }}>
            {isProcessingInvitation && id === selectedUserId ? (
              'Please wait...'
            ) : (
              <>
                <Button
                  sx={{
                    backgroundColor: '#3d5afd',
                    color: 'white',
                    textTransform: 'none',
                    width: '8.125rem',
                    borderRadius: '0rem',
                  }}
                  onClick={() => onAccept(id)}
                >
                  Accept
                </Button>
                <Button
                  sx={{
                    backgroundColor: '#ffffff',
                    color: '#1d1b20',
                    textTransform: 'none',
                    width: '8.125rem',
                    border: '0.0625rem solid #d6d6d6',
                    borderRadius: '0rem',
                  }}
                  onClick={() => onDeny(id)}
                >
                  Deny
                </Button>
              </>
            )}
          </div>
        );
      case 'invited':
        const email = companyMembers.find((user) => user.id === Number(id))?.email;
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.9375rem',
            }}
          >
            <NewButton
              sx={{
                textTransform: 'none',
                width: '10.625rem',
                borderRadius: '0rem',
                backgroundColor: '#e3e3e4',
              }}
              disabled={true}
              type="secondary"
            >
              Invite Sent
            </NewButton>
            <NewButton sx={{ width: '10.625rem' }} type="outline" onClick={() => handleReinviteUser(email ?? '')}>
              Resend Invite
            </NewButton>
          </div>
        );
    }
  };

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'name':
        if (row['firstName'] && row['lastName']) return `${row['firstName']} ${row['lastName']}`;
        else return 'Pending invitation...';
      case 'role':
        return `${row['role'][0].toUpperCase()}${row['role'].substring(1)}`;
      case 'actions':
        return renderAction(row.status, row.id, row.role);
      default:
        return row[column.field];
    }
  };

  const companyPhoneNumber = parsePhoneNumberFromString(companyInfo.companyPhoneNumber, companyInfo.countryCode);

  const normalizeUrl = (url: string): string => {
    if (/^https?:\/\//i.test(url)) {
      return url;
    } else {
      return 'https://' + url;
    }
  };

  return (
    <Box sx={{ padding: '2.5rem 1.5rem' }}>
      <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem' }} fontWeight={400}>
        Company Profile
      </Typography>
      {shouldFetchCompanyInfo ? (
        <CompanyInfoSkeleton />
      ) : (
        <>
          <Box style={{ display: 'flex', gap: '1.5rem', marginTop: '1.5rem' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: '25rem' }}>
              <Section
                icon={<PermIdentity sx={{ marginRight: (theme) => theme.spacing(1) }} />}
                title="Name"
                content={[companyInfo.name]}
                onEdit={() => {}}
              />
              <Section
                icon={<PermIdentity sx={{ marginRight: (theme) => theme.spacing(1) }} />}
                title="Company Phone Number"
                content={[companyPhoneNumber?.formatInternational()]}
                onEdit={() => {}}
              />
              <Section
                icon={<PermIdentity sx={{ marginRight: (theme) => theme.spacing(1) }} />}
                title="Domains"
                headerTooltip={
                  <BkToolTip
                    title="Add domains that are officialy owned and controlled by your organization to ensure users are added appropriately"
                    Icon={InfoOutlinedIcon}
                  />
                }
                content={
                  companyInfo.domains.length
                    ? companyInfo.domains
                        .map((el) => el.name)
                        .join(',\n')
                        .split('\n')
                    : ['-']
                }
                onEdit={() => {}}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', minWidth: '25rem' }}>
              <Section
                icon={<PermIdentity sx={{ marginRight: (theme) => theme.spacing(1) }} />}
                title="Website"
                content={[
                  companyInfo.websiteUrl ? (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={normalizeUrl(companyInfo.websiteUrl)}
                      style={{ color: '#436EB2' }}
                    >
                      {companyInfo.websiteUrl}
                    </a>
                  ) : (
                    '-'
                  ),
                ]}
                onEdit={() => {}}
              />
              <Section
                icon={<PermIdentity sx={{ marginRight: (theme) => theme.spacing(1) }} />}
                title="Address"
                content={[
                  companyInfo.addressOne,
                  companyInfo.addressTwo,
                  `${companyInfo.city}, ${companyInfo.state} ${companyInfo.zipCode}`,
                ]}
                onEdit={() => {}}
              />
            </Box>
          </Box>
          <Box sx={{ maxWidth: '18.75rem', marginTop: '1.5rem' }}>
            <NewButton type="outline" onClick={() => setShowEditDialog(true)}>
              <EditOutlinedIcon />
              <Spacer w={8} />
              Edit Company Profile
            </NewButton>
          </Box>
        </>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '1.875rem',
        }}
      >
        <div style={{ margin: 0 }}>
          <h2 style={{ fontWeight: 500, marginBottom: 0 }}>Users</h2>
          <p style={{ fontSize: '0.875rem' }}>
            {companyMembers.length} {companyMembers.length < 2 ? 'person' : 'people'} in organization
          </p>
        </div>
        <Box sx={{ minWidth: '18.75rem' }}>
          <NewButton type="secondary" onClick={() => setShowNewUser(true)}>
            <AddOutlinedIcon />
            <Spacer w={8} />
            Add New
          </NewButton>
        </Box>
      </div>
      <BkTable
        data={companyMembers}
        columns={columns}
        renderCell={renderCell}
        initialRowsPerPage={10}
        renderPagination={false}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {selectedUserRole &&
          menuItems(selectedUserRole, selectedUserId!).map((item, index) => (
            <span key={item.label} style={{ display: 'flex', alignItems: 'center' }}>
              <MenuItem disabled={item.disabled} onClick={item.onClick}>
                {item.label}
              </MenuItem>
              {item.tooltip && (
                <BkToolTip
                  title={item.disabled && item.tooltip ? item.tooltip : ''}
                  Icon={HelpOutlineIcon}
                  sx={{ margin: '0.5rem' }}
                />
              )}
            </span>
          ))}
      </Menu>
      <AddNewCompanyUser
        open={showNewUser}
        onClose={() => {
          setShowNewUser(false);
        }}
        onSubmit={handleAddNewUser}
      />
      <EditCompanyData
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        onSubmit={onEdit}
        info={{
          ...companyInfo,
          companyPhoneNumber: format(companyInfo.companyPhoneNumber, {
            mask: countryPhoneInfo[companyInfo.countryCode as keyof typeof countryPhoneInfo].mask,
            replacement: { _: /\d/ },
          }),
        }}
      />
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
    </Box>
  );
};

export default CompanyTab;
